/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {AxiosInstance} from 'axios';
import {get} from '../environment/environment';
import {error} from '../logger/logger';
import {
  GenericObject,
  SFTPConnection
} from '../types/entities';
import {safeJSONParse} from '../utils/JSONUtils';
import {createAsyncClient} from './AsyncClientManager';
import {client, config} from './ConnectUtils';
import {getAuthorizationHeader} from './ReportUtils';
import {SisenseOptions, SisenseRole} from './SisenseUtils';

/**
 * Defines a reference to the configured Reports Axios HTTP client instance.
 */
let _reportsClient: AxiosInstance;

// defines the type definitions for getSisenseIFrameURl method...
export interface GetSisenseIFrameURLType {
  serverInfo: GenericObject;
  hasSessionCreated?: boolean;
  dashboardId: string;
};

// defines the type definitions for switchSisenseRole method...
export interface SwitchSisenseRoleParams {
  accountId: number;
  role: SisenseRole;
};

/**
 * Defines the session id to be used for authenticating with Reports Services.
 *
 * @SESSION_ID
 * @type {string}
 */
export const SESSION_ID = Math.random().toString(36).slice(2);

/**
 * Parses the connect response JSON document and returns the value.
 *
 * @method  parse
 * @param   {object} response the returned service response to be parsed.
 * @returns {object} The JSON representation of the service response.
 */
export const parse = (response) => {
  return response.text().then((text) => {
    if (text) {
      const resp = safeJSONParse(text);
      return resp?.results || resp;
    }
    return {};
  });
};

/**
 * Returns the default Connect Service request options
 * based on the given environment (i.e. environment.json configuration).
 *
 * @returns {object} an object representing the service request options.
 */
export const OPTIONS = {
  method      : 'GET',
  mode        : 'cors',
  cache       : 'no-cache',
  credentials : 'include',
  headers     : {
    'Content-Type' : 'application/xml'
  }
};

/**
 * @function registerToken
 *
 * Initiates the registration of the Connect session with the reporting
 * platform.
 *
 *  ```typescript
 *    import {registerToken} from '@ion/core/src/services/Reports';
 *
 *    const token = await registerToken();
 *
 * ```
 */
export const registerToken = async() => {
  const url = `${get('reportsServiceURL')}/registertoken/${get('reportsServiceId')}/${SESSION_ID}`;
  const result = await client.get(url);
  return result;
};

/**
 * The getSisenseOptions method provides the second step of initialization of the
 * Sisense platform. This method handles the retrieval of the authenticated
 * users Sisense configuration based on the given account Id with user settings.
 *
 * ```typescript
 *
 *  import {getSisenseOptions} from '@ion/core/src/services/Reports';
 *
 *  const options = await getSisenseOptions(accountId);
 *
 * ```
 */
export const getSisenseOptions = async(accountId, settings: GenericObject = {}) => {
  const url = `${get('reportsServiceURL')}/sisenseOptions/?accountId=${accountId}`;
  const result = await client.post(url, JSON.stringify({settings}), {
    headers : {
      // eslint-disable-next-line quote-props
      'Authorization' : `Bearer ${SESSION_ID}`
    }
  });
  return result;
};

/**
 * The getSisenseSession method provides step for creating theSisense session.
 * in the client side. This method handles the creating the sessions and cookies of
 * the authenticated users Sisense configuration.
 *
 * ```typescript
 *
 *  import {getSisenseSession} from '@ion/core/src/services/Reports';
 *
 *  await getSisenseSession(options);
 *
 * ```
 */
export const getSisenseSession = async({ssoJwtUrl, jwtToken}: SisenseOptions) => {
  const url    = `${ssoJwtUrl}?jwt=${jwtToken}`;
  const result = await getAxiosClient().get(url, {
    maxRedirects : 0,
    headers      : {
      'content-type' : 'application/json'
    }
  });
  return result;
};

/**
 * The switchSisenseRole function is used to switch the role of a user in Sisense
 * for a specific account. This function sends a request to a Sisense endpoint to
 * change the user's role.
 *
 * ```typescript
 *
 *  import {switchSisenseRole} from '@ion/core/src/services/Reports';
 *
 *  await switchSisenseRole(5099, 'designer');
 *
 * ```
 *
 * @param accountId The unique identifier of the account for which the user's role should be changed.
 * @param role The new role to assign to the user. It can be one of the following roles: 'viewer' or 'designer'.
 * @returns result of the role switch operation
 */
export const switchSisenseRole = async({
  accountId,
  role
}: SwitchSisenseRoleParams) => {
  const url = `${get('reportsServiceURL')}/sisenseOptions/changeUserRole?role=${role}&accountId=${accountId}`;
  const result = await client.post(url, {}, {
    headers : {
      // eslint-disable-next-line quote-props
      'Authorization' : `Bearer ${SESSION_ID}`
    }
  });
  return result;
};

/**
 * The expireSisenseJWT method provides step for logout of the Sisense platform.
 * This method handles the clearing the sessions and cookies of
 * the authenticated users Sisense configuration.
 *
 * ```typescript
 *
 *  import {expireSisenseJWT} from '@ion/core/src/services/Reports';
 *
 *  await expireSisenseJWT(options);
 *
 * ```
 */
export const expireSisenseJWT = async({logoutUrl}) => {
  await client.get(logoutUrl);
};

/**
 * This method is used to make a API call to sisense server with default header
 * for restoring the Sisense dashboard to it's shared state in initial load.
 *
 * ```typescript
 *
 *  import {restoreDashboard} from '@ion/core/src/services/Reports';
 *
 *  await restoreDashboard(dashboardId);
 *
 * ```
 */
export const restoreDashboard = async(dashboardId: string) => {
  try {
    const uri = `${get('sisenseBaseURL')}/api/dashboards/${dashboardId}/restore`;
    await getAxiosClient().get(uri);
  } catch (e) {
    error(`Restore dashboard is failed for dashboard: ${dashboardId} and reason: ${e.message}`);
  }
};

/**
 * This method is used to make a API call to sisense server with default header
 * for expire the session or logout the user. This method itself will handles
 * all exceptions that should not disturb the application.
 *
 *
 * ```typescript
 *
 *  import {logout} from '@ion/core/src/services/Reports';
 *
 *  await logout();
 *
 * ```
 */
export const logout = async() => {
  try {
    await getAxiosClient().get(`${get('sisenseBaseURL')}/api/v1/authentication/logout`);
  } catch (err) {
    error(`Clearing sisense session is failed. Reason: ${err.message}`);
  }
};

/**
 * The function takes an accountId (number or string) and an api function as parameters.If an error
 * occurs during the API call and the error message includes 'login to that account first',
 * the function will attempt to get Sisense options for the specified account to create the account entry
 * at the Sisense and then retry the API call.
 *
 * ```typescript
 *
 *  import {verifySisenseAccountEntry} from '@ion/core/src/services/Reports';
 *
 *  await verifySisenseAccountEntry(5010, api);
 *
 * ```
 * @param accountId - The ID of the account
 * @param api - The API function to be executed.
 */
export const verifySisenseAccountEntry = async(
  accountId: number | string,
  api: () => any
): Promise<void> => {
  try {
    return await api();
  } catch (error) {
    if (
      error?.response?.data?.message?.includes('login to that account first') ||
      error?.message?.includes('login to that account first')
    ) {
      // Retry the API call after getting Sisense options for the specified account
      await getSisenseOptions(accountId);
      return await api();
    }
    throw new Error(error);
  }
};

/**
 * The getSFTPConfigurations function is an asynchronous function that
 * retrieves SFTP configurations for a given account ID from the meta server.
 *
 * ```typescript
 *
 *  import {getSFTPConfigurations} from '@ion/core/src/services/Reports';
 *
 *  const configurations = await getSFTPConfigurations();
 *
 * ```
 */
export const getSFTPConfigurations = async(accountId: number | string) => {
  return verifySisenseAccountEntry(accountId, async() => {
    const url = `${get('reportsServiceURL')}/sisenseOptions/sftpServers?accountId=${accountId}`;
    const result = await client.get(url, {
      headers : getAuthorizationHeader(config, SESSION_ID)
    });
    return result;
  });
};

/**
 * The createSFTPServer function is an asynchronous function that
 * saves the SFTP configurations for a given account ID on the meta server.
 *
 * ```typescript
 *
 *  import {createSFTPServer} from '@ion/core/src/services/Reports';
 *
 *  await createSFTPServer();
 *
 * ```
 */
export const createSFTPServer = async(accountId: number | string, configuration: SFTPConnection | {}) => {
  return verifySisenseAccountEntry(accountId, async() => {
    const url = `${get('reportsServiceURL')}/sisenseOptions/sftpServers?accountId=${accountId}`;
    const result = await client.post(url, JSON.stringify(configuration), {
      headers : getAuthorizationHeader(config, SESSION_ID)
    });
    return result;
  });
};

/**
 * The deleteSFTPServer function is an asynchronous function that
 * removes the SFTP configurations for a given account ID on the meta server.
 *
 * ```typescript
 *
 *  import {deleteSFTPServer} from '@ion/core/src/services/Reports';
 *
 *  await deleteSFTPServer(serverName);
 *
 * ```
 */
export const deleteSFTPServer = async(accountId: number | string, serverName: string) => {
  const url = `${get('reportsServiceURL')}/sisenseOptions/sftpServers?accountId=${accountId}&serverName=${serverName}`;
  const result = await client.delete(url, {
    headers : getAuthorizationHeader(config, SESSION_ID)
  });
  return result;
};

/**
 * The getSisenseIFrameURL function construct the url for sisense
 * dashboard based on it's session.
 *
 * ```typescript
 *
 *  import {getSisenseIFrameURL} from '@ion/core/src/services/Reports';
 *
 *  const url = getSisenseIFrameURL({serverInfo, dashboardId:'61ae95d512b1a20037dbb6bd'});
 *
 * ```
 */
export const getSisenseIFrameURL = ({
  serverInfo,
  dashboardId,
  hasSessionCreated = false
}: GetSisenseIFrameURLType) => {
  const {baseReportURL, ssoJwtUrl, reportRequestParameters, jwtToken} = serverInfo;
  let path = `${baseReportURL}${dashboardId}?${reportRequestParameters}`;
  if (!hasSessionCreated) {
    path = `${ssoJwtUrl}?jwt=${jwtToken}&return_to=${encodeURIComponent(path)}`;
  }
  return path;
};

/**
 *
 * The getAxiosClient function is a private function to retrieve a singleton
 * AxiosInstance for reports.
 *
 */
const getAxiosClient = (): AxiosInstance => {
  if (!_reportsClient) {
    _reportsClient = createAsyncClient(null);
  }
  return _reportsClient;
};
